import { SearchSignatoryPatternResponseDto } from "@/state/signatory/response/search.signatory.pattern.response.dto";
import {
  requestSearchSignatoryPattern,
  requestSearchSignatoryPicture,
} from "@/state/signatory/SignatoryEffects";
import {
  resetTransaction,
  setCurrentTransactionDetail,
  setCurrentTransactionField,
  setSignatureRequirementMode,
  setTransactionAttachments,
  setTransactionFiles,
  setTransactionIsFromDraft,
  setTransactionSignatories,
  setTransactionSignatures,
  setTransactionType,
} from "@/state/transactions/CurrentTransactionEvents";
import {
  requestGetTransactionDetail,
  requestGetTransactionDetailWithFileData,
  requestLaunchTransaction,
  requestUpdateTransaction,
} from "@/state/transactions/TransactionEffects";
import {
  Document,
  File,
  FilterDate,
  Filters,
  Page,
  Signatory,
  Signature,
} from "@/type/core/dashboard/dashboard.types";
import { ModelDocument, ModelSignature } from "@/type/core/models/models.types";
import { Office, Picture } from "@/type/core/offices/offices.types";
import { SignaturePicture } from "@/type/core/signatory/signatory.types";
import {
  TransactionDetail,
  TransactionSearch,
  TransactionStatus,
  TransactionType,
} from "@/type/core/transaction/transaction.types";
import { SignaturePatternEnum } from "@/utils/enums/signature/signature.pattern.enum";
import {
  numberArrayToBase64,
  numberArrayToFile,
} from "@/utils/helpers/base64-helper";
import { toastError } from "@/utils/helpers/toast-helper";
import i18n from "@/utils/i18n/i18n";
import {
  DATES_FILTER,
  DEFAULT_SIGNATURE_REQUIREMENT_MODE,
  EMAIL_FOR_SEALING,
} from "@utils/Constant";
import { SignaturePicturePriorityEnum } from "@utils/enums/signature.picture.priority.enum";
import SignatureHelper from "@utils/helpers/signature-helper";
import { ParsedResponse } from "@utils/rest/ServerResponseParse";

export class TransactionHelper {
  static getSignaturesFromModelTransaction(
    transactionType: TransactionType,
    modelDocuments: ModelDocument[],
    modelSignatures?: ModelSignature[],
    modelSignatories?: Signatory[],
  ): Signature[] {
    if (!modelSignatures || modelSignatures.length === 0) {
      return [];
    }

    const signaturesFromModel: Signature[] = [];
    if (transactionType === "signature") {
      const signatures: Signature[] = modelSignatures.map((signature) => {
        const signatory = signature.modelSignatoryId
          ? modelSignatories?.find(
              (signatory) =>
                signatory.id == signature.modelSignatoryId.toString(),
            )
          : null;
        return {
          modelDocumentId: signature.modelDocumentId,
          email: signatory?.email,
          positionX: signature.positionX,
          positionY: signature.positionY,
          page: signature.page,
          name: signature.name,
          isMoved: signature.isMoved,
        };
      });
      modelDocuments.forEach((document) => {
        signaturesFromModel.push(
          ...signatures.filter(
            (signature) => signature.modelDocumentId === document.id,
          ),
        );
      });
    } else if (transactionType === "scellement") {
      const scellement = modelSignatures[0];
      const isMoved =
        scellement.isMoved ??
        (scellement.positionX > 0 || scellement.positionY > 0);
      signaturesFromModel.push({
        positionX: scellement.positionX,
        positionY: scellement.positionY,
        page: scellement.page,
        name: scellement.name,
        isMoved: isMoved,
        pattern: SignaturePatternEnum.SEAL,
        email: "scellement",
      });
    }
    return signaturesFromModel;
  }

  static getFilesFromModelDocuments(modelDocuments: ModelDocument[]): File[] {
    return modelDocuments.map((document) => {
      const currentSize = document.dataOrigin ? document.dataOrigin.length : 0;
      const file = numberArrayToFile(
        document.dataOrigin ?? [],
        document.name,
        "application/pdf",
      );

      return {
        key: document.name + Date.now().toString(),
        name: document.name,
        modelDocumentId: document.id,
        fileName: document.name,
        base64Data: numberArrayToBase64(document.dataOrigin ?? []),
        size: currentSize,
        rank: document.rank,
        path: document.path,
        url: URL.createObjectURL(file),
        file,
      };
    });
  }

  static getAttachmentsFromModelAttachments(
    modelAttachments: ModelDocument[],
  ): File[] {
    return modelAttachments.map((attachment) => {
      const currentSize = attachment.dataOrigin
        ? attachment.dataOrigin.length
        : 0;
      const file = numberArrayToFile(
        attachment.dataOrigin ?? [],
        attachment.name,
        "application/pdf",
      );

      return {
        key: attachment.name + Date.now().toString(),
        name: attachment.name,
        fileName: attachment.name,
        data: numberArrayToBase64(attachment.dataOrigin ?? []),
        size: currentSize,
        rank: attachment.rank,
        url: URL.createObjectURL(file),
        file,
      };
    });
  }

  static getFilesFromDocuments(documents: Document[], reset = false): File[] {
    return documents.map((document) => {
      const currentSize = document.data ? document.data.length : 0;
      const file = numberArrayToFile(
        document.data ?? [],
        document.fileName,
        "application/pdf",
      );
      // Correctif pour les éditeurs qui positionneraient en dessous de 0 via l'API éditeur et qui après passe par la page front /positioning
      const initialFields = document.initialFields;
      if (initialFields && initialFields.y < 0) {
        initialFields.y = 0;
      }

      return {
        key: document.fileName + Date.now().toString(),
        name: document.fileName,
        fileName: document.fileName,
        id: document.id,
        base64Data: numberArrayToBase64(document.data ?? []),
        size: currentSize,
        rank: document.rank,
        path: document.path,
        type: "application/pdf",
        url: URL.createObjectURL(file),
        file,
        initialFields: reset ? undefined : initialFields,
        signatures: document.signatures,
      };
    });
  }

  static getSignaturesFromFiles(
    files: File[],
    transactionType: TransactionType,
    signatories: Signatory[],
    reset?: boolean,
  ): Signature[] {
    if (transactionType === "scellement") {
      return files
        .map((file, index) =>
          (file.signatures ?? []).map((signature) => {
            const defaultRank =
              signature.signaturePriorityRule ===
              SignaturePicturePriorityEnum.SCELLEMENT_PICTURE
                ? 1
                : 0;
            const finalSignature: Signature = {
              ...signature,
              rank: index + 1,
              page: reset ? 0 : signature.page,
              positionX: reset
                ? 0
                : (signature.positionX ?? 0) * window.devicePixelRatio,
              positionY: reset
                ? 0
                : (signature.positionY ?? 0) * window.devicePixelRatio,
              isMoved:
                !reset &&
                (signature.isMoved ||
                  (signature.positionX != undefined &&
                    signature.positionX > 0) ||
                  (signature.positionY != undefined &&
                    signature.positionY > 0)),
              signaturePictureRank:
                signature.signaturePictureRank ?? defaultRank,
              signaturePriorityRule:
                signature.signaturePriorityRule || undefined,
              pattern: signature.pattern || SignaturePatternEnum.SEAL,
              email: EMAIL_FOR_SEALING,
              identificator: SignatureHelper.buildSignatureIdentificator(
                index,
                0,
                0,
              ),
            };
            return finalSignature;
          }),
        )
        .flat();
    }

    let signaturesToReturn: Signature[] = [];
    files.map((file, index) => {
      signatories.forEach((signatory, signatoryIndex) => {
        let signatorySignaturesForFile =
          file.signatures?.filter(
            (signature) => signature.email === signatory.email,
          ) ?? [];
        signatorySignaturesForFile = signatorySignaturesForFile.map(
          (signature, signatureIndex) => {
            const finalSignature: Signature = {
              ...signature,
              rank: index + 1,
              positionX: reset
                ? 0
                : (signature.positionX ?? 0) * window.devicePixelRatio,
              positionY: reset
                ? 0
                : (signature.positionY ?? 0) * window.devicePixelRatio,
              isMoved:
                !reset &&
                (signature.isMoved ||
                  (signature.positionX != undefined &&
                    signature.positionX > 0) ||
                  (signature.positionY != undefined &&
                    signature.positionY > 0)),
              identificator: SignatureHelper.buildSignatureIdentificator(
                index,
                signatoryIndex,
                signatureIndex,
              ),
            };

            return finalSignature;
          },
        );

        const unMovedSignature = {
          ...signatorySignaturesForFile[0],
          positionX: 0,
          positionY: 0,
          isMoved: false,
          identificator: SignatureHelper.buildSignatureIdentificator(
            index,
            signatoryIndex,
            signatorySignaturesForFile.length,
          ),
        };
        if (reset) {
          signaturesToReturn = signaturesToReturn.concat(
            signatorySignaturesForFile.slice(0, 1),
          );
        } else {
          signaturesToReturn = signaturesToReturn.concat(
            signatorySignaturesForFile,
            [unMovedSignature],
          );
        }
      });
    });

    return signaturesToReturn;
  }

  static retrieveTransactionDetailAndSetItIntoStore(
    transactionId: string,
    callback?: (
      transaction: TransactionDetail,
      signatures: Signature[],
    ) => void,
    reset?: boolean,
  ): void {
    resetTransaction();
    void requestGetTransactionDetailWithFileData({
      id: transactionId,
    }).then((result) => {
      if (result.ok && result.data) {
        const transactionType: TransactionType = result.data.transaction
          .type as TransactionType;
        setTransactionType(transactionType);
        setTransactionSignatories(result.data.signatories);

        const files = TransactionHelper.getFilesFromDocuments(
          result.data.documents.sort((a, b) =>
            TransactionHelper.documentSorter(a, b),
          ),
          reset,
        );
        setTransactionFiles(files);
        const transactionSignatures = TransactionHelper.getSignaturesFromFiles(
          files,
          transactionType,
          result.data.signatories,
          reset,
        );
        setTransactionSignatures(transactionSignatures);
        setTransactionAttachments(
          TransactionHelper.getFilesFromDocuments(result.data.attachments),
        );
        setCurrentTransactionField([
          {
            field: "office",
            value: result.data.transaction.officeId,
          },
          {
            field: "confidential",
            value: result.data.transaction.confidential,
          },
          {
            field: "authorizedExpertForConfidentiality",
            value: result.data.authorizedExperts,
          },
          {
            field: "extraRecipients",
            value: result.data.transaction.extraRecipients,
          },
          {
            field: "reference",
            value: result.data.transaction.object,
          },
          {
            field: "invitationMailConfiguration",
            value: result.data.transaction.invitationMailConfiguration,
          },
          {
            field: "endOfTransactionMailConfiguration",
            value: result.data.transaction.endOfTransactionMailConfiguration,
          },
          {
            field: "invitationMode",
            value: result.data.transaction.invitationMode,
          },
          {
            field: "id",
            value: result.data.transaction.id,
          },
        ]);
        setTransactionIsFromDraft(true);
        setCurrentTransactionDetail(result.data.transaction);
        setSignatureRequirementMode(
          result.data.transaction.signatureRequirementMode ??
            DEFAULT_SIGNATURE_REQUIREMENT_MODE,
        );

        callback?.(result.data.transaction, transactionSignatures);
      } else {
        toastError(
          "Erreur lors de la récupération des détails de la transaction",
        );
      }
    });
  }

  static updateTransaction = (
    transactionId: string,
    transaction: TransactionDetail,
    status: TransactionStatus,
    cause: string | undefined,
    setIsLoading: (isLoading: boolean) => void,
    callbackOnSuccess?: () => void,
  ): void => {
    const newTransaction: TransactionDetail = {
      ...transaction,
      confidential: undefined,
      status: status,
      cause: cause,
    };
    setIsLoading(true);
    requestUpdateTransaction({
      id: newTransaction.id,
      dto: {
        status: newTransaction.status,
        reference: newTransaction.object,
        invitationMailConfiguration: newTransaction.invitationMailConfiguration,
        endOfTransactionMailConfiguration:
          newTransaction.endOfTransactionMailConfiguration,
        office: newTransaction.officeId,
        cause: newTransaction.cause,
        confidential: newTransaction.confidential,
        extraRecipients: transaction.extraRecipients,
        invitationMode: newTransaction.invitationMode,
      },
    })
      .then((result) => {
        if (result.ok && result.data) {
          callbackOnSuccess?.();
        } else {
          toastError(i18n.t("error.transaction.update", { transactionId }));
        }
      })
      .catch(() => {
        toastError(i18n.t("error.transaction.update", { transactionId }));
      })
      .finally(() => setIsLoading(false));
  };

  static launchTransaction = (
    transaction: TransactionDetail,
    callbackOnSuccess?: (transactionId: string, direct: boolean) => void,
    relaunch: boolean = false,
    direct: boolean = false,
  ): void => {
    const transactionId = transaction.id;
    void requestLaunchTransaction({
      id: transactionId,
      dto: {
        message: transaction.message ?? "",
        relaunch: relaunch,
        direct: direct,
      },
    })
      .then((result) => {
        if (result.ok && result.data) {
          callbackOnSuccess?.(transactionId, direct);
        } else if (result.responseCode === 409) {
          void requestGetTransactionDetail({ id: transactionId });
        } else {
          const errorMessage =
            result.data && result.data.details
              ? `: ${result.data.details}`
              : "";
          toastError(
            `${i18n.t("error.transactionDetail.retrieve", { transactionId })}${errorMessage}`,
          );
        }
      })
      .catch((error) => {
        const errorMessage =
          error.data && error.data.details ? `: ${error.data.details}` : "";
        toastError(
          `${i18n.t("error.transaction.send", { transactionId })}${errorMessage}`,
        );
      });
  };

  private static _handleSetTransactionSignatureInformations = (
    currentTransactionSignatories: Signatory[],
    currentTransactionSignatures: Signature[],
    pictures: Picture[],
    signaturePriority: SignaturePicturePriorityEnum,
    pattern: SignaturePatternEnum,
    signatoryEmail: string,
  ): void => {
    const signatoriesWithCorrectInformations =
      currentTransactionSignatories.map((currentSignatory) => {
        if (currentSignatory.email === signatoryEmail) {
          currentSignatory.signaturePicture = pictures;
          currentSignatory.pattern = pattern;
          currentSignatory.signaturePriority = signaturePriority;
        }
        return currentSignatory;
      });
    const signaturesWithCorrectPattern = currentTransactionSignatures.map(
      (currentSignature) => {
        if (currentSignature.email === signatoryEmail) {
          currentSignature.pattern = pattern;
        }
        return currentSignature;
      },
    );

    if (signatoriesWithCorrectInformations.length) {
      setTransactionSignatories(signatoriesWithCorrectInformations);
    }

    if (signaturesWithCorrectPattern.length) {
      setTransactionSignatures(signaturesWithCorrectPattern);
    }
  };

  static retrieveTransactionSignatoriesPicturesAndCertificate = (
    currentTransactionSignatories: Signatory[],
    currentTransactionSignatures: Signature[],
    officeId: string,
    callback?: () => void,
  ): void => {
    currentTransactionSignatories.forEach((signatory) => {
      const pattern =
        signatory.pattern ??
        SignatureHelper.getSignaturePatternFromEmail(
          currentTransactionSignatures,
          signatory.email,
        );
      Promise.all([
        requestSearchSignatoryPicture({
          dto: {
            mail: encodeURIComponent(signatory.email),
            officeid: officeId,
          },
        }),
        requestSearchSignatoryPattern({
          dto: {
            mail: encodeURIComponent(signatory.email),
            officeid: officeId,
            pattern,
          },
        }),
      ])
        .then((responses) => {
          const pictureRes: ParsedResponse<SignaturePicture> = responses[0];
          const patternRes: ParsedResponse<SearchSignatoryPatternResponseDto> =
            responses[1];

          if (
            pictureRes.ok &&
            pictureRes.data &&
            patternRes.ok &&
            patternRes.data
          ) {
            const pictureArray: Picture[] =
              pictureRes.data.signaturePicture ?? [];
            if (pictureRes.data.guestSignaturePicture) {
              pictureArray.push(pictureRes.data.guestSignaturePicture);
            }
            this._handleSetTransactionSignatureInformations(
              currentTransactionSignatories,
              currentTransactionSignatures,
              pictureArray,
              pictureRes.data.signaturePriority?.toUpperCase() as SignaturePicturePriorityEnum,
              patternRes.data.pattern,
              signatory.email,
            );
          } else {
            toastError(
              `${i18n.t("error.toast.signatureDetail")} ${signatory.email}`,
            );
          }
        })
        .catch(() => {
          toastError(
            `${i18n.t("error.toast.signatureDetail")} ${signatory.email}`,
          );
        })
        .finally(() => callback?.());
    });
  };

  static buildBodyGetTransaction(
    selected: Filters,
    search: string,
    page: Page,
    sort: { order: number; type: string },
    shortcutName: string,
  ): TransactionSearch {
    // collect transaction types
    const transactionTypes: TransactionType[] = [];
    if (selected.signatureCheck) transactionTypes.push("signature");
    if (selected.scellementCheck) transactionTypes.push("scellement");

    // build optional date periods
    let expirationPeriod: FilterDate | null = null;
    let lastModificationPeriod: FilterDate | null = null;

    const datePeriod = selected.datePeriod;
    if (datePeriod) {
      const begin = datePeriod.beginPeriod;
      const end = datePeriod.endPeriod;

      if (selected.selectedDateButton === DATES_FILTER.EXPIRATION) {
        expirationPeriod = { beginPeriod: begin, endPeriod: end };
      } else if (
        selected.selectedDateButton === DATES_FILTER.LAST_MODIFICATION
      ) {
        lastModificationPeriod = { beginPeriod: begin, endPeriod: end };
      }
    }

    // assemble payload
    return {
      shortcut: shortcutName,
      page: page.current,
      size: page.size,
      q: search,
      sort: sort,
      offices: selected.cabinet.map((office: Office) => office.id),
      status: selected.status,
      types: transactionTypes,
      isHideConfidential: true,
      expirationPeriod,
      lastModificationPeriod,
    };
  }

  static documentSorter(documentA: Document, documentB: Document): number {
    const rankA = documentA.rank;
    const rankB = documentB.rank;

    if (rankA === rankB) {
      return 0;
    }

    return rankA < rankB ? -1 : 1;
  }

  static eachSignatoryHasOnSignatureOnEachDocument(
    nbSignatories: number,
    nbDocuments: number,
    nbSignatures: number,
  ): boolean {
    return nbDocuments * nbSignatories === nbSignatures;
  }
}
